import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FlightTakeoff as OooIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { outOfOfficeApi } from '../../api/services';
import type { OutOfOffice } from '../../types/models';

// ---------------------------------------------------------------------------
// OutOfOfficeCard — small Preferences card listing upcoming OOO windows with
// add / remove controls. Each window decides two flags: whether the daily
// briefing email should be suppressed (default true) and whether the
// scheduling engine should block work allocation (default true). Phase 3
// task 3.11.
// ---------------------------------------------------------------------------

const REASONS = ['Holiday', 'Travel', 'Training', 'Sick', 'Other'] as const;

function formatWindow(entry: OutOfOffice): string {
  const start = new Date(entry.startDate);
  const end = new Date(entry.endDate);
  if (entry.startDate === entry.endDate) {
    return format(start, 'EEE d MMM yyyy');
  }
  return `${format(start, 'd MMM')} to ${format(end, 'd MMM yyyy')}`;
}

export default function OutOfOfficeCard() {
  const [entries, setEntries] = useState<OutOfOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    reason: 'Holiday',
    notes: '',
    suppressBriefing: true,
    blockWorkAllocation: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Show today + the next ~6 months. Older entries fall off.
      const today = format(new Date(), 'yyyy-MM-dd');
      const futureCutoff = format(
        new Date(Date.now() + 180 * 24 * 60 * 60 * 1000),
        'yyyy-MM-dd',
      );
      const data = await outOfOfficeApi.getActive(today, futureCutoff);
      setEntries(data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load out-of-office.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () =>
    setForm({
      startDate: '',
      endDate: '',
      reason: 'Holiday',
      notes: '',
      suppressBriefing: true,
      blockWorkAllocation: true,
    });

  const handleAdd = async () => {
    if (!form.startDate || !form.endDate) return;
    setSaving(true);
    setError(null);
    try {
      await outOfOfficeApi.create(form);
      setOpen(false);
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save out-of-office.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await outOfOfficeApi.delete(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not delete out-of-office.');
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <OooIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Out of office</Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add window
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mark days as out of office to keep Start My Day from allocating work, and to stop the daily briefing email landing in your inbox.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No out-of-office windows scheduled.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {entries.map((entry) => (
              <Stack
                key={entry.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 1, border: 1, borderColor: 'divider', borderRadius: 1.5 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatWindow(entry)}
                    </Typography>
                    <Chip label={entry.reason} size="small" />
                    {!entry.blockWorkAllocation && (
                      <Chip label="Working day" size="small" variant="outlined" />
                    )}
                    {!entry.suppressBriefing && (
                      <Chip label="Digest still sent" size="small" variant="outlined" />
                    )}
                  </Stack>
                  {entry.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {entry.notes}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label="Delete out-of-office window"
                  onClick={() => handleDelete(entry.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>

      <Dialog open={open} onClose={saving ? undefined : () => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add out of office</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="date"
                size="small"
                fullWidth
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="End"
                type="date"
                size="small"
                fullWidth
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
            <Select
              size="small"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            >
              {REASONS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
            <TextField
              label="Notes"
              size="small"
              multiline
              minRows={2}
              placeholder="Optional context, e.g. 'annual leave — phone on airplane mode'"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.blockWorkAllocation}
                  onChange={(e) => setForm((f) => ({ ...f, blockWorkAllocation: e.target.checked }))}
                />
              }
              label={<Typography variant="body2">Block Start My Day from allocating work</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.suppressBriefing}
                  onChange={(e) => setForm((f) => ({ ...f, suppressBriefing: e.target.checked }))}
                />
              }
              label={<Typography variant="body2">Suppress the daily briefing email</Typography>}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={saving || !form.startDate || !form.endDate}
          >
            {saving ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
